/**
 * 智能体配置模态框
 *
 * 支持创建和编辑智能体，包括：
 * - 名称输入
 * - 提示词编辑
 * - 温度调节
 * - 工具选择
 * - 模式选择（primary/subagent/all）
 * - 技能选择
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAgentStore } from '../../store/agentStore';
import type { Agent, ToolName, AgentMode } from '../../types/agent';
import { ALL_TOOLS, getToolCategory } from '../../types/agent';
import { SkillSelector } from '../skill';
import { SkillPreviewModal } from '../skill';
import { validateAgentName } from '../../utils/validation';

interface AgentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent?: Agent | null; // 如果提供，则为编辑模式
  onSuccess: () => void;
}

interface FormData {
  name: string;
  prompt: string;
  temperature: number;
  enabled_tools: ToolName[];
  mode: AgentMode;
  llm_config: Record<string, unknown> | null;
  enabled_skills: string[];
  subagent_invocation_timing: string;
}

const DEFAULT_FORM_DATA: FormData = {
  name: '',
  prompt: '',
  temperature: 0.7,
  enabled_tools: [...ALL_TOOLS],
  mode: 'primary',
  llm_config: null,
  enabled_skills: [],
  subagent_invocation_timing: '',
};

export const AgentConfigModal: React.FC<AgentConfigModalProps> = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}) => {
  const { createAgent, updateAgent, loading, error } = useAgentStore();

  // 表单数据
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [previewSkillName, setPreviewSkillName] = useState<string | null>(null);

  // 是否为编辑模式
  const isEditMode = !!agent;

  // 当agent变化时，填充表单数据
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name,
        prompt: agent.prompt,
        temperature: agent.temperature,
        enabled_tools: [...agent.enabled_tools],
        mode: agent.mode,
        llm_config: agent.llm_config,
        enabled_skills: [...agent.enabled_skills],
        subagent_invocation_timing: agent.subagent_invocation_timing || '',
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
    setValidationErrors({});
  }, [agent, isOpen]);

  // 验证表单
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    const nameResult = validateAgentName(formData.name);
    if (!nameResult.valid) {
      errors.name = nameResult.message || '名称无效';
    }

    if (!formData.prompt.trim()) {
      errors.prompt = '提示词不能为空';
    }

    if (!formData.prompt.trim()) {
      errors.prompt = '提示词不能为空';
    }

    if (formData.temperature < 0 || formData.temperature > 2) {
      errors.temperature = '温度必须在0-2之间';
    }

    if (formData.enabled_tools.length === 0) {
      errors.enabled_tools = '至少选择一个工具';
    }

    // 如果是subagent或all模式，验证调用时机说明
    if (formData.mode === 'subagent' || formData.mode === 'all') {
      if (!formData.subagent_invocation_timing || !formData.subagent_invocation_timing.trim()) {
        errors.subagent_invocation_timing = '子智能体模式必须填写调用时机说明';
      } else if (formData.subagent_invocation_timing.trim().length < 10) {
        errors.subagent_invocation_timing = '调用时机说明至少需要10个字符';
      } else if (formData.subagent_invocation_timing.length > 500) {
        errors.subagent_invocation_timing = '调用时机说明不能超过500个字符';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // 处理提交
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const skillData = {
        name: formData.name,
        prompt: formData.prompt,
        temperature: formData.temperature,
        enabled_tools: formData.enabled_tools,
        mode: formData.mode,
        llm_config: formData.llm_config,
        enabled_skills: formData.enabled_skills,
        subagent_invocation_timing: formData.subagent_invocation_timing || null,
      };

      if (isEditMode && agent) {
        // 编辑模式
        await updateAgent(agent.id, skillData);
      } else {
        // 创建模式
        await createAgent(skillData);
      }
      onSuccess();
    } catch (err) {
      console.error('保存智能体失败:', err);
    }
  }, [formData, isEditMode, agent, validateForm, updateAgent, createAgent, onSuccess]);

  // 处理工具选择
  const handleToolToggle = useCallback((tool: ToolName) => {
    setFormData(prev => {
      const enabled_tools = prev.enabled_tools.includes(tool)
        ? prev.enabled_tools.filter(t => t !== tool)
        : [...prev.enabled_tools, tool];
      return { ...prev, enabled_tools };
    });
  }, []);

  // 按类别分组工具
  const toolsByCategory = ALL_TOOLS.reduce((acc, tool) => {
    const category = getToolCategory(tool);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tool);
    return acc;
  }, {} as Record<string, ToolName[]>);

  // 模式选项
  const modeOptions: { value: AgentMode; label: string; description: string }[] = [
    { value: 'primary', label: '主智能体', description: '用户可直接选择使用' },
    { value: 'subagent', label: '子智能体', description: '仅能被其他智能体调用' },
    { value: 'all', label: '通用模式', description: '同时支持两种使用方式' },
  ];

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* 标题 */}
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {isEditMode ? '编辑智能体' : '新建智能体'}
          </h2>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* 表单 */}
          <form onSubmit={handleSubmit}>
            {/* 名称 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
                placeholder="智能体名称（最多 5 个汉字或 10 个英文字母）"
                maxLength={20}
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
              )}
              <p className="mt-1 text-xs text-gray-600">
                名称只能包含汉字或英文字母，不能超过 5 个汉字或 10 个英文字母
              </p>
            </div>

            {/* 模式选择 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                智能体模式
              </label>
              <div className="flex gap-3">
                {modeOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex-1 p-3 rounded-lg border cursor-pointer transition-colors
                      ${formData.mode === option.value
                        ? 'bg-paper-50 border-paper-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="mode"
                      value={option.value}
                      checked={formData.mode === option.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value as AgentMode }))}
                      className="sr-only"
                    />
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </label>
                ))}
              </div>
            </div>

            {/* 调用时机说明（仅subagent/all模式显示） */}
            {(formData.mode === 'subagent' || formData.mode === 'all') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  调用时机说明
                </label>
                <textarea
                  value={formData.subagent_invocation_timing}
                  onChange={(e) => setFormData(prev => ({ ...prev, subagent_invocation_timing: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent min-h-[60px]"
                  placeholder="描述何时应该调用此子智能体..."
                  maxLength={500}
                />
                {validationErrors.subagent_invocation_timing && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.subagent_invocation_timing}</p>
                )}
                <p className="mt-1 text-xs text-gray-600">
                  帮助主智能体判断何时调用此子智能体（10-500字符）
                </p>
              </div>
            )}

            {/* 提示词 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                提示词 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent min-h-[120px]"
                placeholder="描述智能体的角色和行为方式..."
              />
              {validationErrors.prompt && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.prompt}</p>
              )}
            </div>

            {/* 温度 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                温度: {formData.temperature.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-800 mt-1">
                <span>精确 (0.0)</span>
                <span>创意 (2.0)</span>
              </div>
              {validationErrors.temperature && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.temperature}</p>
              )}
            </div>

            {/* 工具选择 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                可用工具 <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {Object.entries(toolsByCategory).map(([category, tools]) => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-gray-800 mb-1 capitalize">
                      {category === 'read' ? '读取工具' : '写入工具'}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tools.map((tool) => (
                        <label
                          key={tool}
                          className={`
                            inline-flex items-center px-3 py-1.5 rounded-full text-sm cursor-pointer
                            transition-colors duration-150
                            ${formData.enabled_tools.includes(tool)
                              ? 'bg-paper-100 text-paper-800 border border-paper-300'
                              : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                            }
                          `}
                        >
                          <input
                            type="checkbox"
                            checked={formData.enabled_tools.includes(tool)}
                            onChange={() => handleToolToggle(tool)}
                            className="sr-only"
                          />
                          {tool}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {validationErrors.enabled_tools && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.enabled_tools}</p>
              )}
            </div>

            {/* 技能选择 */}
            <div className="mb-6">
              <SkillSelector
                enabledSkills={formData.enabled_skills}
                onChange={(skills) => setFormData(prev => ({ ...prev, enabled_skills: skills }))}
                onPreviewSkill={setPreviewSkillName}
              />
            </div>

            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-md"
                disabled={loading}
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md disabled:opacity-50"
                disabled={loading}
              >
                {loading ? '保存中...' : (isEditMode ? '保存' : '创建')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 技能预览模态框 */}
      <SkillPreviewModal
        isOpen={!!previewSkillName}
        skillName={previewSkillName}
        onClose={() => setPreviewSkillName(null)}
      />
    </>
  );
};

export default AgentConfigModal;